import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, MapPin, Heart, MessageCircle, Video, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-rose-950/20 via-transparent to-transparent" />
      
      {/* Header */}
      <header className="relative z-10 px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame className="w-8 h-8 text-rose-500" />
          <span className="font-outfit text-2xl font-bold text-white tracking-tight">LocalFlame</span>
        </div>
        <Button
          data-testid="login-btn-header"
          variant="ghost"
          className="text-zinc-400 hover:text-white"
          onClick={() => navigate('/login')}
        >
          Sign In
        </Button>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 px-6 pt-12 pb-24">
        <div className="max-w-lg mx-auto text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-rose-500/10 text-rose-500 text-xs font-bold uppercase tracking-[0.2em] mb-6">
            No Monthly Fees
          </span>
          
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-medium text-white tracking-tighter mb-6 leading-[1.1]">
            Meet Local Singles
            <span className="block text-rose-500">Tonight</span>
          </h1>
          
          <p className="text-zinc-400 text-lg mb-10 leading-relaxed max-w-md mx-auto">
            Connect with real people in your area. One payment, all features unlocked forever.
            No subscriptions. No hidden costs.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button
              data-testid="get-started-btn"
              size="lg"
              className="bg-rose-600 hover:bg-rose-500 text-white font-semibold px-8 py-6 text-lg rounded-full shadow-[0_8px_32px_rgba(225,29,72,0.3)] transition-all hover:shadow-[0_8px_40px_rgba(225,29,72,0.5)] hover:scale-105"
              onClick={() => navigate('/register')}
            >
              Get Started — $19.99
            </Button>
            <Button
              data-testid="learn-more-btn"
              variant="outline"
              size="lg"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800/50 px-8 py-6 text-lg rounded-full"
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
            >
              Learn More
            </Button>
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-12 text-center">
            <div>
              <div className="text-3xl font-outfit font-bold text-white">25mi</div>
              <div className="text-sm text-zinc-500">Max Distance</div>
            </div>
            <div>
              <div className="text-3xl font-outfit font-bold text-white">$19.99</div>
              <div className="text-sm text-zinc-500">One-Time Fee</div>
            </div>
            <div>
              <div className="text-3xl font-outfit font-bold text-white">100%</div>
              <div className="text-sm text-zinc-500">Features Unlocked</div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <section id="features" className="max-w-4xl mx-auto mt-24">
          <h2 className="font-outfit text-2xl sm:text-3xl font-medium text-white text-center mb-12">
            Everything You Need to Connect
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={<MapPin className="w-6 h-6" />}
              title="Local Only"
              description="Only match with people within 25 miles. No long-distance games."
            />
            <FeatureCard
              icon={<Heart className="w-6 h-6" />}
              title="Swipe & Match"
              description="Simple swipe interface. Like someone? Swipe right. It's that easy."
            />
            <FeatureCard
              icon={<MessageCircle className="w-6 h-6" />}
              title="Instant Chat"
              description="Message your matches instantly. No waiting, no limits."
            />
            <FeatureCard
              icon={<Video className="w-6 h-6" />}
              title="Video Calls"
              description="See them before you meet. Video call your matches anytime."
            />
            <FeatureCard
              icon={<Shield className="w-6 h-6" />}
              title="No Subscriptions"
              description="Pay once, use forever. All features included. No tricks."
            />
            <FeatureCard
              icon={<Flame className="w-6 h-6" />}
              title="Hot Matches"
              description="Smart matching finds you the best local connections."
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-2xl mx-auto mt-24 text-center">
          <div className="bg-gradient-to-b from-zinc-900/50 to-zinc-900/30 rounded-3xl p-10 border border-zinc-800/50">
            <h2 className="font-outfit text-2xl sm:text-3xl font-medium text-white mb-4">
              Ready to Find Your Match?
            </h2>
            <p className="text-zinc-400 mb-8">
              Join thousands of locals looking for real connections.
            </p>
            <Button
              data-testid="cta-get-started-btn"
              size="lg"
              className="bg-rose-600 hover:bg-rose-500 text-white font-semibold px-10 py-6 text-lg rounded-full shadow-[0_8px_32px_rgba(225,29,72,0.3)]"
              onClick={() => navigate('/register')}
            >
              Start Now — One-Time $19.99
            </Button>
            <p className="text-zinc-600 text-sm mt-4">
              No recurring charges. Ever.
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-8 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-rose-500" />
            <span className="font-outfit font-semibold text-white">LocalFlame</span>
          </div>
          <p className="text-zinc-600 text-sm">
            © 2025 LocalFlame. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 hover:border-zinc-700/50 transition-colors">
      <div className="w-12 h-12 rounded-xl bg-rose-500/10 flex items-center justify-center text-rose-500 mb-4">
        {icon}
      </div>
      <h3 className="font-outfit text-lg font-medium text-white mb-2">{title}</h3>
      <p className="text-zinc-500 text-sm leading-relaxed">{description}</p>
    </div>
  );
}
